<template>
  <main class="min-h-screen">
    <slot name="header" />

    <aside
      v-if="tableOfContentsHeadings.length"
      ref="pageContentSwitcherRef"
      class="page-content-switcher page-content-switcher-padding flex-col gap-5 sticky top-0 bg-mbzuai-navy-500 z-100"
    >
      <ClientOnly>
        <ContentContainer class="relative">
          <div class="grid py-6 grid-cols-[1fr_auto] gap-3 items-center">
            <h5 class="text-h5 relative overflow-hidden col-start-1 col-span-1">
              <span class="hidden md:inline">{{ $t('You are viewing') }}: </span>
              <Transition name="slide-up">
                <span
                  :key="activeHeadingId as string"
                  class="text-h5-light font-bold md:font-light"
                >
                  {{
                    tableOfContentsHeadings.find(
                      (heading) => heading.id === activeHeadingId
                    )?.text
                  }}
                </span>
              </Transition>
            </h5>

            <div
              class="flex gap-2 col-start-2 col-span-1 justify-between items-center bg- lg:bg-none cursor-pointer"
              role="button"
              tabindex="0"
              aria-label="Open Content"
              @click="handleOpenContents"
              @keydown.enter="handleOpenContents"
              @keydown.space.prevent="handleOpenContents"
            >
              <h6 class="text-h6 text-mbzuai-sand-500! hidden lg:block">
                {{ $t('Show all content') }}
              </h6>

              <span
                class="bg-mbzuai-sand-500 rounded-full inline-flex w-8 h-8 shrink-0 items-center justify-center lg:bg-transparent lg:w-auto lg:h-auto lg:rounded-none"
              >
                <ChevronIcon
                  :direction="isContentsOpen ? 'up' : 'down'"
                  severity="dark"
                  class="hidden lg:block"
                />
                <ChevronIcon
                  :direction="isContentsOpen ? 'up' : 'down'"
                  severity="dark"
                  colour="text-mbzuai-navy-500"
                  class="block lg:hidden"
                />
              </span>
            </div>
          </div>
          <div class="progress-bar w-full h-[5px] absolute bottom-0 left-0" />
        </ContentContainer>

        <ContentContainer
          class="absolute left-0 right-0 z-50 bg-mbzuai-navy-500"
        >
          <section
            :class="
              isContentsOpen
                ? 'h-auto! w-full max-h-none py-6'
                : 'h-0 overflow-hidden'
            "
          >
            <ul
              class="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-start align-top"
            >
              <li v-for="heading in tableOfContentsHeadings" :key="heading.id">
                <a
                  href="#"
                  class="transition-colors no-underline! link hover:underline! decoration-mbzuai-sand-500! decoration-2 underline-offset-3 focus-visible:no-underline focus-visible:decoration-0 focus-visible:outline-0 focus-visible:bg-(--tabbed-view) focus-visible:border-b-[3px] focus-visible:border-black! focus-visible:text-mbzuai-navy-500! p-1 lg:p-2 rounded-sm inline-block"
                  :class="
                    heading.id === activeHeadingId
                      ? 'bg-mbzuai-sand-500 text-mbzuai-navy-500! text-body-small-medium'
                      : 'bg-mbzuai-navy-500 text-body-small'
                  "
                  @click.prevent="handleTOCChange(heading.id)"
                >
                  {{ heading.text }}
                </a>
              </li>
            </ul>
          </section>
        </ContentContainer>
      </ClientOnly>
    </aside>

    <div ref="contentRef" class="relative z-10 bg-white scroll-container">
      <slot name="content" />
    </div>

    <SiteFooter />
  </main>
</template>

<script setup lang="ts">
const contentRef = ref<HTMLElement | null>(null);
const pageContentSwitcherRef = ref<HTMLElement | null>(null);
const tableOfContentsListRef = ref<HTMLElement | null>(null);
const getPageContentSwitcherOffset = () =>
  (pageContentSwitcherRef.value?.getBoundingClientRect().height ?? 0) + 25;

let tableOfContentsHeadings = ref<Array<{ id: string; text: string }>>([]);
let activeHeadingId = ref<string | null>(null);

let scrollToHeading = (_id: string) => {};
let init = () => {};
let refresh = () => {};

if (import.meta.client) {
  ({
    tableOfContentsHeadings,
    activeHeadingId,
    scrollToHeading,
    init,
    refresh,
  } = useHeadingToc(contentRef, tableOfContentsListRef, {
    scrollSpyOffsetTop: getPageContentSwitcherOffset,
    selectScrollPadding: getPageContentSwitcherOffset,
    programmaticLockMs: 700,
    observeCharacterData: true,
  }));
}

const isClientReady = ref(false);
const hasRenderedHeadings = computed(() => {
  if (!import.meta.client || !contentRef.value) return false;
  return contentRef.value.querySelector('h2') !== null;
});

const contentReady = computed(
  () => isClientReady.value && hasRenderedHeadings.value
);

const isContentsOpen = ref(false);
const handleOpenContents = () => {
  isContentsOpen.value = !isContentsOpen.value;
};

const handleTOCChange = (value: string) => {
  if (!value) return;
  activeHeadingId.value = value as string;
  scrollToHeading(value);
  handleOpenContents();
};

onMounted(async () => {
  isClientReady.value = true;
  await nextTick();
});

watch(
  contentReady,
  (ready) => {
    if (!ready) return;
    init();
    refresh();
  },
  { immediate: true }
);
</script>

<style>
.page-content-switcher-padding > .content-container {
  padding-inline: 1rem;
}

@media (min-width: 48rem) {
  .page-content-switcher-padding > .content-container {
    padding-inline: 42px !important;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

/* incoming: starts below, slides up into place */
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(100%);
}

/* outgoing: slides up and out */
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

/* detach leaving element from flow so incoming doesn't get pushed */
.slide-up-leave-active {
  position: absolute;
}

.scroll-container {
  position: relative;
}

.progress-bar {
  background-color: var(--p-brand-sand-500);
  transform-origin: left;

  animation: track-progress linear;
  animation-timeline: scroll(root block);
  animation-fill-mode: both;
}

@keyframes track-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
</style>
